import { useTranslator } from 'components/shared/hooks';
import { useEnvironment } from 'components/shared/hooks/useEnvironment/useEnvironment';
import React from 'react';
import { useStyles } from 'components/shared/layout/Footer/Footer.styles';
import classNames from 'classnames';

const SVG_DATA_URI_PREFIX = 'data:image/svg+xml';

function sanitizeInlineSvg(svg: string): string {
  // Minimal sanitization for SVG injected into DOM.
  // We remove scripts and common executable vectors.
  return svg
    .replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, '')
    .replace(/<style[\s\S]*?>[\s\S]*?<\/style>/gi, '')
    .replace(/\son\w+\s*=\s*(['"])[\s\S]*?\1/gi, '')
    .replace(/javascript:/gi, '')
    .replace(/<foreignObject[\s\S]*?>[\s\S]*?<\/foreignObject>/gi, '');
}

function getInlineSvgFromDataUri(dataUri: string): { innerHtml: string; viewBox?: string } | null {
  if (!dataUri || !dataUri.startsWith(SVG_DATA_URI_PREFIX)) return null;

  const commaIdx = dataUri.indexOf(',');
  if (commaIdx === -1) return null;

  const meta = dataUri.substring(0, commaIdx).toLowerCase();
  const payload = dataUri.substring(commaIdx + 1);

  let decoded: string;
  try {
    if (meta.includes(';base64')) {
      // base64 payload
      decoded = typeof atob === 'function' ? atob(payload) : '';
    } else {
      // urlencoded payload, e.g. "...,%3Csvg%20..."
      decoded = decodeURIComponent(payload);
    }
  } catch {
    return null;
  }

  if (!decoded) return null;

  const sanitized = sanitizeInlineSvg(decoded);

  const viewBox = sanitized.match(/viewBox\s*=\s*(['"])(.*?)\1/i)?.[2];

  // Extract inner content of the first <svg>...</svg>.
  const innerHtml = sanitized
    .replace(/^[\s\S]*?<svg\b[^>]*>/i, '')
    .replace(/<\/svg>[\s\S]*$/i, '')
    .trim();

  if (!innerHtml) return null;

  return { innerHtml, viewBox };
}

export const Footer = () => {
  const { banner } = useEnvironment();

  const classes = useStyles({ bannerHeight: banner.imageHeight });
  const trans = useTranslator();

  const inlineSvg = React.useMemo(() => getInlineSvgFromDataUri(banner.imageUrl), [banner.imageUrl]);

  return (
    <div className={classNames('container-fluid', classes.container)}>
      {banner.imageUrl && !banner.link && (
        inlineSvg ? (
          <svg
            className={classes.image}
            xmlns="http://www.w3.org/2000/svg"
            viewBox={inlineSvg.viewBox}
            role="img"
            aria-label={trans('FOOTER.BANNER')}
            dangerouslySetInnerHTML={{ __html: inlineSvg.innerHtml }}
          />
        ) : (
          <img src={banner.imageUrl} alt={trans('FOOTER.BANNER')} className={classes.image} />
        )
      )}

      {banner.imageUrl && banner.link && (
        <a href={banner.link} target="_blank" rel="noopener noreferrer">
          {inlineSvg ? (
            <svg
              className={classes.image}
              xmlns="http://www.w3.org/2000/svg"
              viewBox={inlineSvg.viewBox}
              role="img"
              aria-label={trans('FOOTER.BANNER')}
              dangerouslySetInnerHTML={{ __html: inlineSvg.innerHtml }}
            />
          ) : (
            <img src={banner.imageUrl} alt={trans('FOOTER.BANNER')} className={classes.image} />
          )}
        </a>
      )}
    </div>
  );
};
